import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { hasFeature } from "../../../lib/features";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Flask, MagnifyingGlass, Eye, DownloadSimple, Sparkle } from "@phosphor-icons/react";
import { Pill, Spinner } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { LibraryPreview, statsText, err } from "./libraryUtils";

/** Debounced lookup of published library COSHH assessments matching a product name. */
export function useCoshhMatches(q, enabled = true) {
  const [state, setState] = useState({ items: [], loading: false, term: "" });
  useEffect(() => {
    const term = (q || "").trim();
    if (!enabled || term.length < 2) { setState({ items: [], loading: false, term }); return undefined; }
    let stale = false;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      API.get("/hs/library", { params: { kind: "coshh", q: term } }).then((r) => { if (!stale) setState({ items: r.data.items || [], loading: false, term }); }).catch(() => { if (!stale) setState({ items: [], loading: false, term }); });
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [q, enabled]);
  return state;
}

/** Rows of matching library assessments with Preview / Use; `onUsed` fires after the copy lands. */
export function CoshhMatchList({ items, canEdit, onUsed }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const open = (item) => { setPreview({ ...item, loading: true }); API.get(`/hs/library/${item.id}`).then((r) => setPreview(r.data)).catch((e) => { err(e, "Couldn't open the preview"); setPreview(null); }); };
  const use = async (item) => {
    setBusy(item.id);
    try {
      const { data } = await API.post(`/hs/library/${item.id}/use`);
      toast.success(data.message);
      setPreview(null); onUsed?.();
      navigate(data.url);
    } catch (e) { err(e, "Couldn't copy this into your workspace"); } finally { setBusy(""); }
  };
  if (!items.length) return null;
  return (
    <>
      <ul className="divide-y divide-border rounded-md border border-border" data-testid="coshh-match-list">
        {items.map((it) => (
          <li key={it.id} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`coshh-match-${it.id}`}>
            <Flask size={16} className="text-amber-600 shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm font-medium leading-tight">{it.title}</p>
              <p className="text-[11px] text-muted-foreground">{[it.subject, it.trade, statsText(it)].filter(Boolean).join(" · ")}</p>
            </div>
            {it.my_copies > 0 && <Pill tone={TONE_CLASSES.ok}>In your workspace</Pill>}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => open(it)} data-testid={`coshh-match-preview-${it.id}`}><Eye size={12} className="mr-1" /> Preview</Button>
            {canEdit && <Button size="sm" className="h-7 text-xs" disabled={busy === it.id} onClick={() => use(it)} data-testid={`coshh-match-use-${it.id}`}><DownloadSimple size={12} className="mr-1" /> {busy === it.id ? "Copying…" : "Use this"}</Button>}
          </li>
        ))}
      </ul>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden break-words p-4 sm:p-6 grid-cols-[minmax(0,1fr)]" data-testid="coshh-match-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">{preview.title}</DialogTitle>
                <DialogDescription>{preview.summary || statsText(preview)}</DialogDescription>
              </DialogHeader>
              {preview.loading ? <Spinner /> : <LibraryPreview item={preview} />}
              <DialogFooter className="gap-2 sm:gap-0 sticky -bottom-4 sm:-bottom-6 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-background border-t border-border">
                <Button variant="outline" onClick={() => setPreview(null)} data-testid="coshh-match-preview-close">Close</Button>
                {canEdit && <Button disabled={busy === preview.id || preview.loading} onClick={() => use(preview)} data-testid="coshh-match-preview-use"><DownloadSimple size={14} className="mr-1" /> {busy === preview.id ? "Copying…" : "Use in my workspace"}</Button>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** COSHH folder search box: find a ready-made library assessment by product before drafting a new one. */
export function CoshhProductSearch({ canEdit, onDraftNew }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const enabled = hasFeature(user, "hs_library");
  const { items, loading, term } = useCoshhMatches(q, enabled);
  if (!enabled) return null;
  return (
    <div className="rounded-lg border border-amber-300/70 dark:border-amber-700/60 bg-amber-50/50 dark:bg-amber-950/20 p-4 space-y-3" data-testid="coshh-product-search">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Flask size={16} weight="fill" className="text-amber-600" /> Check the Template library first</p>
          <p className="text-[11px] text-muted-foreground">Type the product or substance — if the platform team already holds a ready-made COSHH assessment for it, copy that instead of drafting from scratch.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. isopropyl alcohol, PVC cement, bleach…" className="h-9 pl-8" data-testid="coshh-product-search-input" />
        </div>
      </div>
      {loading && <p className="text-xs text-muted-foreground" data-testid="coshh-product-search-loading">Searching…</p>}
      {!loading && term.length >= 2 && items.length > 0 && <p className="text-xs text-muted-foreground" data-testid="coshh-product-search-count">{items.length} ready-made {items.length === 1 ? "assessment matches" : "assessments match"} “{term}”</p>}
      <CoshhMatchList items={items} canEdit={canEdit} />
      {!loading && term.length >= 2 && !items.length && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="coshh-product-search-empty">
          <span className="text-muted-foreground">No ready-made assessment for “{term}” yet.</span>
          {canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onDraftNew?.(term)} data-testid="coshh-product-search-draft"><Sparkle size={12} weight="fill" className="mr-1" /> Draft one with AI</Button>}
        </div>
      )}
    </div>
  );
}
